/* eslint-disable @typescript-eslint/no-explicit-any */
import { toProductDataItem } from 'layout/navigation/search/utils'
import omit from 'lodash/omit'
import { useRouter } from 'next/router'

import { ParsedUrlQuery } from 'querystring'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SortOption, SortOptionLabel } from 'search'
import { ConsumerSearchDesktopGridLayout } from 'search/components/DesktopSearchGridLayout'
import { ConsumerSearchMobileGridLayout } from 'search/components/MobileSearchGridLayout'
import { SearchGridContextProps } from 'search/components/SearchGridContext'
import { SearchResultsDisplay } from 'search/components/SearchResults'
import GridHeader from 'shared/components/GridHeader'
import useCurrency from 'shared/hooks/useCurrency'
import { ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { SearchState } from 'shared/types/Search'
import {
  ConsumerSearchFilters,
  getClientConsumerSearchFilters,
  transformAggregationsToStructuredData,
} from 'shared/utils/search/cs-utils'
import { urlToSearchState } from 'shared/utils/search/search-url-utils'

export type GridConfig = {
  context: SearchGridContextProps
  regex?: RegExp
  indexPath?: string
}

export type GridProps = {
  /** CS search state with just base refinement lists (used in PLPs).
   * @example
   *  /adidas PLP ==> {brand_name: ['adidas']}
   *  /apparel PLP ==> {product_category: ['clothing', 'bags', 'accessories']}
   * */
  baseCSSearchState?: SearchState
  /** Grid configuration */
  config: GridConfig
  /** Consumer Search results */
  consumerSearchResult?: ProductSearchResponse
  /** Consumer Search search state for the Grid  */
  consumerSearchState: SearchState
  /** Consumer Search trending results */
  consumerSearchTrendingResults?: ProductSearchResponse
  /** Description for the grid header */
  description?: string
  /** Darwin group from SSR for Size Filter Placement experiment */
  ssrDarwinGroup?: string // TODO: remove once Size Filter Placement Experiment is finished
  /** Title for the grid */
  title: string
}

const Grid = ({
  baseCSSearchState,
  config,
  consumerSearchResult,
  consumerSearchState,
  consumerSearchTrendingResults,
  description,
  title,
  ssrDarwinGroup,
}: GridProps) => {
  const { selectedCurrency, loading: isLoadingCurrency } = useCurrency()
  const [isCurrencyInitialized, setIsCurrencyInitialized] = useState(false)
  const [modelFilters, setModelFilters] = useState(consumerSearchResult?.allFilters || {})
  const [searchState, setSearchState] = useState<SearchState>(consumerSearchState)
  const [currentConsumerSearchResult, setCurrentConsumerSearchResult] =
    useState(consumerSearchResult)
  const router = useRouter()
  const refQueryString = useRef(getQueryString(router.query))

  // Fetch Consumer Search data
  const fetchClientConsumerSearchData = useCallback(
    async (currentSearchParams: ConsumerSearchFilters) => {
      const url = '/api/product-search?' + new URLSearchParams(currentSearchParams).toString()
      const { data, error } = await fetch(url, {
        method: 'GET',
      })
        .then(async (res) => {
          return res.json()
        })
        .catch(() => {
          return {
            data: {},
            error: 'error fetching data',
            total: 0,
          }
        })
      // here if router query changes then make call to consumer search api
      const newConsumerSearchResult = {
        error: error || null,
        data: toProductDataItem(data?.productsList || []),
        total: data?.totalResults || 0,
        aggregation: {
          filters: transformAggregationsToStructuredData(data?.aggregationValuesList || []),
        },
      }

      setCurrentConsumerSearchResult(newConsumerSearchResult)
    },
    [],
  )

  useEffect(() => {
    if (selectedCurrency && !isLoadingCurrency) {
      setIsCurrencyInitialized(true)
    }
  }, [isLoadingCurrency])

  // Consumer Search fetch data effect
  useEffect(() => {
    const isNewQueryString = getQueryString(router.query) !== refQueryString.current
    if ((searchState && isNewQueryString) || (isCurrencyInitialized && selectedCurrency)) {
      refQueryString.current = getQueryString(router.query)
      const currentSearchParams = getClientConsumerSearchFilters(
        baseCSSearchState || {},
        (router.query as Record<string, string>) || {},
        { silhouettes: true },
      )
      void fetchClientConsumerSearchData(currentSearchParams)
    }
    // Only fetch from client when the search state or router query changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.refinementList, selectedCurrency])

  // Update CS search state with refinementList from URL query
  useEffect(() => {
    const qsRefinementList = urlToSearchState(router.query).refinementList
    setSearchState((prevSearchState) => ({ ...prevSearchState, refinementList: qsRefinementList }))
  }, [router.query])

  // Update CS search state when consumerSearchState changes (i.e. nav between PLPs)
  useEffect(() => {
    setSearchState(consumerSearchState)
  }, [consumerSearchState])

  // Update current consumer search result when consumerSearchResult changes (i.e. nav between PLPs)
  useEffect(() => {
    setCurrentConsumerSearchResult(consumerSearchResult)
    setModelFilters(consumerSearchResult?.allFilters || {})
  }, [consumerSearchResult])

  // ======== RENDER SEARCH RESULTS ========

  return (
    <SearchResultsDisplay
      noResultsState={consumerSearchTrendingResults?.data as any}
      searchResults={currentConsumerSearchResult?.data as any}
      searchState={searchState}
    >
      <GridHeader description={description} title={title} />
      {modelFilters && (
        <ConsumerSearchMobileGridLayout
          consumerResults={currentConsumerSearchResult}
          defaultRefinement={router.query.sortBy || searchState.index || SortOption.Popular}
          hiddenFilters={config.context.hiddenFilters}
          initialFilters={modelFilters}
          initialSearchState={searchState}
          nbHits={currentConsumerSearchResult?.total}
          sortConfig={sortConfig()}
          ssrDarwinGroup={ssrDarwinGroup}
        />
      )}
      {modelFilters && (
        <ConsumerSearchDesktopGridLayout
          consumerResults={currentConsumerSearchResult}
          defaultRefinement={router.query.sortBy || searchState.index || SortOption.Popular}
          hiddenFilters={config.context.hiddenFilters}
          initialFilters={modelFilters}
          initialSearchState={searchState}
          nbHits={currentConsumerSearchResult?.total}
          sortConfig={sortConfig()}
          ssrDarwinGroup={ssrDarwinGroup}
        />
      )}
    </SearchResultsDisplay>
  )
}
export default Grid

function getQueryString(qs: ParsedUrlQuery) {
  const q = omit(qs, 'slug') // omit slug as it's only relevant for NextJS routing
  return `${new URLSearchParams(q as Record<string, string>).toString()}`
}

function sortConfig() {
  return Object.keys(SortOptionLabel()).map((option) => ({
    value: SortOption[option as keyof typeof SortOption],
    label: SortOptionLabel()[option as keyof ReturnType<typeof SortOptionLabel>],
  }))
}
